/**
 * Badge Display Component
 * Shows a single badge with tooltip and earned/locked states
 */

'use client';

import { useState } from 'react';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'reading' | 'tools' | 'streak' | 'learning' | 'engagement';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  earned: boolean;
  awardedAt?: string | null;
  progress?: number;
  progressPercent?: number;
  requirementValue?: number;
}

interface BadgeDisplayProps {
  badge: Badge;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  showProgress?: boolean;
}

const tierColors = {
  bronze: 'from-amber-700 to-amber-600',
  silver: 'from-gray-400 to-gray-300',
  gold: 'from-yellow-500 to-yellow-400',
  platinum: 'from-purple-500 to-pink-500',
};

const tierBorders = {
  bronze: 'border-amber-600',
  silver: 'border-gray-400',
  gold: 'border-yellow-400',
  platinum: 'border-purple-400',
};

const sizeClasses = {
  sm: 'w-12 h-12 text-xl',
  md: 'w-16 h-16 text-2xl',
  lg: 'w-20 h-20 text-3xl',
};

export default function BadgeDisplay({
  badge,
  size = 'md',
  showTooltip = true,
  showProgress = true,
}: BadgeDisplayProps) {
  const [isHovered, setIsHovered] = useState(false);

  const earned = badge.earned;
  const tierColor = tierColors[badge.tier];
  const tierBorder = tierBorders[badge.tier];

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Badge Icon */}
      <div
        className={`
          ${sizeClasses[size]}
          rounded-full
          flex items-center justify-center
          border-2
          transition-all duration-200
          ${
            earned
              ? `bg-gradient-to-br ${tierColor} ${tierBorder} shadow-lg`
              : 'bg-surface border-border opacity-40 grayscale'
          }
          ${isHovered && earned ? 'scale-110 shadow-xl' : ''}
        `}
      >
        <span className={earned ? '' : 'opacity-50'}>{badge.icon}</span>
      </div>

      {/* Progress ring for unearned badges */}
      {!earned && showProgress && badge.progressPercent !== undefined && badge.progressPercent > 0 && (
        <svg
          className={`absolute inset-0 ${sizeClasses[size].split(' ')[0]} ${sizeClasses[size].split(' ')[1]}`}
          viewBox="0 0 36 36"
        >
          <path
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="rgba(232, 152, 65, 0.3)"
            strokeWidth="2"
          />
          <path
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#E89841"
            strokeWidth="2"
            strokeDasharray={`${badge.progressPercent}, 100`}
            strokeLinecap="round"
          />
        </svg>
      )}

      {/* Tooltip */}
      {showTooltip && isHovered && (
        <div
          className="
            absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2
            bg-background border border-border rounded-lg shadow-xl
            px-3 py-2 min-w-[180px]
            animate-in fade-in slide-in-from-bottom-1 duration-200
          "
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{badge.icon}</span>
            <span className="font-semibold text-foreground text-sm">{badge.name}</span>
          </div>
          <p className="text-foreground-secondary text-xs">{badge.description}</p>
          {earned && badge.awardedAt && (
            <p className="text-accent text-xs mt-1">
              Earned {new Date(badge.awardedAt).toLocaleDateString()}
            </p>
          )}
          {!earned && badge.progress !== undefined && badge.requirementValue && (
            <p className="text-foreground-tertiary text-xs mt-1">
              Progress: {badge.progress}/{badge.requirementValue}
            </p>
          )}
          {/* Tooltip arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-background border-b border-r border-border rotate-45" />
        </div>
      )}
    </div>
  );
}
